import React, { useState, useCallback, useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight, X, Tag } from 'lucide-react'
import { shareService } from '../services/shareService'
import { satsueiDataTorokuService } from '../services/satsueiDataTorokuService'
import { useBlobUrlFromApi } from '../hooks/useBlobUrl'

const ImageCarousel = ({ images, sharePixId, isOpen, onClose, objectId, showTags = false, imageService, serviceParams = [], initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const carouselRef = useRef(null)

  // Update currentIndex when carousel opens or initialIndex changes
  useEffect(() => {
    if (isOpen && images && images.length > 0) {
      // Ensure initialIndex is within valid range
      const validIndex = Math.max(0, Math.min(initialIndex, images.length - 1))
      setCurrentIndex(validIndex)
    }
  }, [isOpen, initialIndex, images])

  // Prevent scrolling on parent container when carousel is open
  useEffect(() => {
    if (isOpen && carouselRef.current) {
      // Find the scrollable parent container (media display area)
      const scrollableParent = carouselRef.current.closest('[data-testid="media-display-area"]')
      if (scrollableParent) {
        // Store current scroll position
        const scrollTop = scrollableParent.scrollTop
        const originalOverflow = scrollableParent.style.overflow
        
        // Prevent scrolling
        scrollableParent.style.overflow = 'hidden'
        
        // Scroll to top to ensure carousel is visible
        scrollableParent.scrollTop = 0
        
        // Cleanup function
        return () => {
          scrollableParent.style.overflow = originalOverflow || ''
          scrollableParent.scrollTop = scrollTop
        }
      }
    }
  }, [isOpen])

  if (!isOpen || !images || images.length === 0) return null

  const currentImage = images[currentIndex]

  // Get image tags as string (only if showTags is true)
  const imageTags = showTags ? (currentImage.imageTagList?.map(tag => tag.imageTag).join(' ') || '') : ''

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    )
  }

  const goToNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    )
  }

  return (
    <div ref={carouselRef} className="absolute inset-0 bg-black bg-opacity-90 z-20 flex items-center justify-center rounded-lg" data-testid="image-carousel">
      <div className="relative w-full h-full p-4">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-30 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-colors"
          data-testid="carousel-close-button"
        >
          <X size={24} />
        </button>

        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-30 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-colors"
              data-testid="carousel-prev-button"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-30 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-colors"
              data-testid="carousel-next-button"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {/* Main image area */}
        <div className="flex items-center justify-center w-full h-full" data-testid="carousel-image-display">
          <ImageDisplay 
            image={currentImage} 
            sharePixId={sharePixId} 
            objectId={objectId} 
            imageService={imageService} 
            serviceParams={[...serviceParams, 'satsueiImage']}
          />
        </div>

        {/* Image Tags Overlay (only if showTags is true) */}
        {showTags && imageTags && (
          <div className="absolute top-4 left-4 z-30 bg-black bg-opacity-70 text-white p-3 rounded-lg max-w-md" data-testid="carousel-tags">
            <div className="flex items-center space-x-2 mb-2">
              <Tag size={16} />
              <span className="text-sm font-medium">タグ:</span>
            </div>
            <div className="text-sm break-words">{imageTags}</div>
          </div>
        )}

        {/* Image counter */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded text-sm" data-testid="carousel-counter">
          {currentIndex + 1} / {images.length}
        </div>
      </div>
    </div>
  )
}

// Unified component for image display with proper blob URL management
const ImageDisplay = ({ image, sharePixId, imageService, serviceParams = [] }) => {
  // Create stable cache key for the image
  const objectImageEditId = image.objectImageEditId ?? image.maxObjectImageEditId ?? null
  const imageKey = `${image.objectId}-${image.objectImageId}-${objectImageEditId || 'null'}-${image.filenameExtension}-${image.isUploaded || false}-${sharePixId}-${imageService ? 'custom' : 'default'}`

  // Early check to prevent hook from running if we know the image shouldn't be loaded (same as ImageCard)
  const shouldSkipImageLoad = image.isUploaded === 0 || image.isUploaded === "0" || image.isUploaded === false

  // Create stable fetch function with useCallback to prevent infinite re-renders
  const fetchImage = useCallback(async () => {
    try {
      if (image.objectId === null || image.objectId === undefined ||
        image.objectImageId === null || image.objectImageId === undefined ||
        !image.filenameExtension) {
        return null
      }

      // Add timeout to prevent long loading for non-existent images
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Image load timeout')), 5000) // 5 second timeout
      })

      // Use the provided service or fallback to default behavior (same as ImageCard)
      const fetchPromise = (async () => {
        if (imageService && typeof imageService.getImage === 'function') {
          // Use custom service with custom parameters
          return await imageService.getImage(
            image.objectId,
            image.objectImageId,
            objectImageEditId,
            image.filenameExtension,
            image.isUploaded,
            ...serviceParams
          )
        } else {
          // Fallback: use shareService or satsueiDataTorokuService based on sharePixId
          const service = sharePixId ? shareService : satsueiDataTorokuService
          const fallbackServiceParams = sharePixId ? [true, sharePixId] : []
          return await service.getImage(
            image.objectId,
            image.objectImageId,
            objectImageEditId,
            image.filenameExtension,
            image.isUploaded,
            ...fallbackServiceParams
          )
        }
      })()

      // Race between fetch and timeout
      const result = await Promise.race([fetchPromise, timeoutPromise])
      return result
    } catch (error) {
      console.error('Failed to load image:', error)
      return null // Return null instead of throwing to show "no image" state
    }
  }, [image, objectImageEditId, imageService, serviceParams, sharePixId])

  const { blobUrl: imageUrl, isLoading, error } = useBlobUrlFromApi(
    shouldSkipImageLoad ? null : fetchImage,
    [imageKey],
    'image/jpeg'
  )


  if (isLoading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center" data-testid="carousel-loading">
        <div className="text-white text-lg">読み込み中...</div>
      </div>
    )
  }

  if (error || !imageUrl) {
    return (
      <div className="absolute inset-0 flex items-center justify-center" data-testid="carousel-error">
        <div className="text-white text-lg">画像の読み込みに失敗しました</div>
      </div>
    )
  }


  return (
    <img
      src={imageUrl}
      alt={`Image ${image.objectId || 'unknown'}`}
      className="w-full h-full object-contain"
      data-testid="carousel-image"
      onError={(e) => {
        console.error('Image failed to load in img tag', {
          src: imageUrl,
          error: e,
          imageData: image
        })
      }}
      onLoad={() => {
        // Image loaded successfully
      }}
    />
  )
}

export default ImageCarousel
